import { useState, useEffect } from "react";
import { X, ArrowUp, ArrowDown, Trash2, Plus, Link2, Check } from "lucide-react";
import { LEG_COLOURS } from "../../constants.js";
import { toISO, fromISO, addDays } from "../../utils/date.js";
import { buildShareUrl, copyToClipboard } from "../../utils/url.js";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isCloudLeg = (id) => typeof id === "string" && UUID_RE.test(id);

export default function LegsModal({ open, legs, onClose, onSave, tripId }) {
  const [draft, setDraft] = useState([]);
  const [errors, setErrors] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(JSON.parse(JSON.stringify(legs)));
      setErrors([]);
      setCopiedId(null);
    }
  }, [open, legs]);

  if (!open) return null;

  const validate = (list) => {
    const errs = [];
    if (list.length === 0) {
      errs.push("At least one leg is required");
      return errs;
    }
    list.forEach((leg, i) => {
      if (!leg.city?.trim()) errs.push(`Leg ${i + 1}: city is required`);
      if (!leg.country?.trim()) errs.push(`Leg ${i + 1}: country is required`);
      if (!leg.start) errs.push(`Leg ${i + 1}: start date is required`);
      if (!leg.end) errs.push(`Leg ${i + 1}: end date is required`);
      if (leg.start && leg.end && leg.end < leg.start) {
        errs.push(`Leg ${i + 1}: end date must be on or after start date`);
      }
    });
    return errs;
  };

  const updateLeg = (i, patch) => {
    setDraft((d) => d.map((leg, idx) => idx === i ? { ...leg, ...patch } : leg));
  };

  const addLeg = () => {
    const last = draft[draft.length - 1];
    const suggestedStart = last?.end
      ? toISO(addDays(fromISO(last.end), 1))
      : toISO(new Date());
    const suggestedEnd = toISO(addDays(fromISO(suggestedStart), 2));
    const newLeg = {
      id: `leg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      city: "",
      country: "",
      start: suggestedStart,
      end: suggestedEnd,
      emoji: "📍",
      colour: LEG_COLOURS[draft.length % LEG_COLOURS.length],
      tentative: false,
    };
    setDraft((d) => [...d, newLeg]);
  };

  const removeLeg = (i) => {
    const cloudWarning = isCloudLeg(draft[i].id)
      ? " All items in this leg will also be permanently deleted."
      : "";
    if (!window.confirm(`Remove ${draft[i].city || `leg ${i + 1}`}?${cloudWarning}`)) return;
    setDraft((d) => d.filter((_, idx) => idx !== i));
  };

  const moveLeg = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= draft.length) return;
    setDraft((d) => {
      const next = [...d];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const handleSave = () => {
    const errs = validate(draft);
    if (errs.length) { setErrors(errs); return; }
    onSave(draft);
    onClose();
  };

  const handleCopyShare = async (leg) => {
    if (!tripId || !isCloudLeg(leg.id)) return;
    const url = buildShareUrl(tripId, [leg.id]);
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopiedId(leg.id);
      setTimeout(() => setCopiedId((cur) => (cur === leg.id ? null : cur)), 1800);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full sm:max-w-2xl bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "92dvh", height: "92dvh" }}
      >
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              Trip legs
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Cities, dates, and colours</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
        >
          {draft.map((leg, i) => (
            <div
              key={leg.id}
              className="rounded-2xl border bg-slate-50/60 dark:bg-slate-700/60 p-3 space-y-2.5"
              style={{ borderColor: `${leg.colour}40` }}
            >
              <div className="flex items-center gap-2">
                <span className="w-2 h-8 rounded-full flex-shrink-0" style={{ background: leg.colour }} />
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">Leg {i + 1}</span>
                <div className="flex-1" />
                {tripId && isCloudLeg(leg.id) && (
                  <button
                    onClick={() => handleCopyShare(leg)}
                    className="p-1.5 rounded-full hover:bg-white dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400"
                    title="Copy share link for this leg only"
                  >
                    {copiedId === leg.id ? <Check size={14} className="text-emerald-600" /> : <Link2 size={14} />}
                  </button>
                )}
                <button
                  onClick={() => moveLeg(i, -1)}
                  disabled={i === 0}
                  className="p-1.5 rounded-full hover:bg-white dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  onClick={() => moveLeg(i, 1)}
                  disabled={i === draft.length - 1}
                  className="p-1.5 rounded-full hover:bg-white dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  onClick={() => removeLeg(i)}
                  className="p-1.5 rounded-full hover:bg-red-50 text-red-500"
                  title="Remove leg"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Flag</label>
                  <input
                    value={leg.emoji || ""}
                    onChange={(e) => updateLeg(i, { emoji: e.target.value })}
                    placeholder="🇹🇭"
                    maxLength={4}
                    className="w-full px-2 py-2 rounded-lg border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-center text-lg bg-white dark:bg-slate-700 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-5">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1 block">City</label>
                  <input
                    value={leg.city}
                    onChange={(e) => updateLeg(i, { city: e.target.value })}
                    placeholder="London"
                    className="w-full px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-sm bg-white dark:bg-slate-700 dark:text-slate-100"
                  />
                </div>
                <div className="col-span-5">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Country</label>
                  <input
                    value={leg.country || ""}
                    onChange={(e) => updateLeg(i, { country: e.target.value })}
                    placeholder="UK"
                    className="w-full px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-sm bg-white dark:bg-slate-700 dark:text-slate-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Start</label>
                  <input
                    type="date"
                    value={leg.start}
                    onChange={(e) => updateLeg(i, { start: e.target.value })}
                    className="w-full px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-sm bg-white dark:bg-slate-700 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1 block">End</label>
                  <input
                    type="date"
                    value={leg.end}
                    onChange={(e) => updateLeg(i, { end: e.target.value })}
                    className="w-full px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-sm bg-white dark:bg-slate-700 dark:text-slate-100"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Colour</label>
                <div className="flex flex-wrap gap-1.5">
                  {LEG_COLOURS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => updateLeg(i, { colour: c })}
                      className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                      style={{
                        background: c,
                        outline: leg.colour === c ? `2px solid ${c}` : "none",
                        outlineOffset: leg.colour === c ? "2px" : "0",
                      }}
                      title={c}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  id={`tentative-${leg.id}`}
                  type="checkbox"
                  checked={!!leg.tentative}
                  onChange={(e) => updateLeg(i, { tentative: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 dark:border-slate-600"
                />
                <label htmlFor={`tentative-${leg.id}`} className="text-xs text-slate-600 dark:text-slate-400">
                  Tentative (not confirmed yet)
                </label>
              </div>
            </div>
          ))}

          <button
            onClick={addLeg}
            className="w-full px-4 py-3 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-500 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Plus size={16} /> Add a leg
          </button>

          {errors.length > 0 && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3">
              <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="px-5 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex gap-2 justify-end flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-full bg-slate-900 dark:bg-slate-600 text-white font-medium hover:bg-slate-700 dark:hover:bg-slate-500 transition-colors"
          >
            Save legs
          </button>
        </div>
      </div>
    </div>
  );
}
